import { resolveTimeZone, type DueDateInput } from '@/shared/lib/due-date'
import { toDate } from '@/shared/lib/format'

import { BCP47, DEFAULT_LOCALE, type Locale } from './locale'

/**
 * Fechas y números por idioma, con `Intl`.
 *
 * **Nada de formatos armados a mano.** `18/09/2026` y `09/18/2026` son la misma
 * fecha escrita por dos convenciones, y cuál va en cada idioma es un dato que
 * cambia río arriba, no una decisión nuestra. Lo mismo con los miles: `1.500`
 * en español y portugués, `1,500` en inglés. `Intl` trae CLDR, que es la misma
 * fuente que usa `babel` del lado del servidor.
 *
 * Todo lo que mira el calendario recibe `timeZone`: una fecha sin hora
 * (`2026-09-17`) es un DÍA, y pasarla por UTC la corre un día entero en Buenos
 * Aires. Ver `due-date.ts`.
 */

const EMPTY = '—'

const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/

/**
 * Qué se escribe y en qué zona. `2026-09-18` es un día del calendario: se arma
 * al mediodía UTC y se escribe en UTC, así es el 18 en cualquier zona. Leído
 * como medianoche local y escrito en otra zona, se corría un día (en un
 * navegador —o un CI— en UTC, Buenos Aires mostraba el 17).
 */
function diaYZona(value: DueDateInput, timeZone?: string): [Date | null, string] {
  if (typeof value === 'string' && SOLO_FECHA.test(value)) {
    const dia = Date.UTC(
      Number(value.slice(0, 4)),
      Number(value.slice(5, 7)) - 1,
      Number(value.slice(8, 10)),
      12,
    )
    return [new Date(dia), 'UTC']
  }
  return [toDate(value), resolveTimeZone(timeZone)]
}

function cached<T>(map: Map<string, T>, key: string, build: () => T): T {
  let value = map.get(key)
  if (value === undefined) {
    value = build()
    map.set(key, value)
  }
  return value
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()
const numberFormats = new Map<string, Intl.NumberFormat>()
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>()

function dateFormatter(
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
  timeZone: string,
): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`
  return cached(
    dateFormats,
    key,
    () => new Intl.DateTimeFormat(BCP47[locale], { ...options, timeZone }),
  )
}

/**
 * `18/09/2026` en español y portugués, `09/18/2026` en inglés.
 *
 * `2-digit` en día y mes a propósito: la UI las dibuja en mono con
 * `tabular-nums` y un `9/2026` de una fila contra un `18/09/2026` de la de
 * abajo hace bailar la columna.
 */
export function formatDate(
  value: DueDateInput,
  locale: Locale = DEFAULT_LOCALE,
  timeZone?: string,
): string {
  const [date, zona] = diaYZona(value, timeZone)
  if (!date) return EMPTY
  return dateFormatter(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }, zona).format(
    date,
  )
}

/** `17 sep 2026` / `Sep 17, 2026`. Para donde el mes escrito se lee mejor que el número. */
export function formatDateMedium(
  value: DueDateInput,
  locale: Locale = DEFAULT_LOCALE,
  timeZone?: string,
): string {
  const [date, zona] = diaYZona(value, timeZone)
  if (!date) return EMPTY
  return dateFormatter(locale, { day: 'numeric', month: 'short', year: 'numeric' }, zona).format(
    date,
  )
}

/** `18/09/2026 14:30` */
export function formatDateTime(
  value: DueDateInput,
  locale: Locale = DEFAULT_LOCALE,
  timeZone?: string,
): string {
  const date = toDate(value)
  if (!date) return EMPTY
  return dateFormatter(
    locale,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    resolveTimeZone(timeZone),
  ).format(date)
}

/** `1.500` / `1,500`. */
export function formatNumber(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
  options?: Intl.NumberFormatOptions,
): string {
  const key = `${locale}|${JSON.stringify(options ?? {})}`
  return cached(numberFormats, key, () => new Intl.NumberFormat(BCP47[locale], options)).format(
    value,
  )
}

/**
 * Los cortes de `Intl.RelativeTimeFormat`, de más grueso a más fino.
 *
 * Se elige la primera unidad en la que la diferencia da al menos 1, así
 * "hace 40 días" sale como "hace 1 mes" y no como un número que nadie
 * convierte de cabeza.
 */
const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 3600_000],
  ['month', 30 * 24 * 3600_000],
  ['week', 7 * 24 * 3600_000],
  ['day', 24 * 3600_000],
  ['hour', 3600_000],
  ['minute', 60_000],
  ['second', 1000],
]

/** `hace 3 días` / `há 3 dias` / `3 days ago`. */
export function formatRelative(
  value: DueDateInput,
  locale: Locale = DEFAULT_LOCALE,
  now?: Date,
): string {
  const date = toDate(value)
  if (!date) return EMPTY

  const delta = date.getTime() - (now ?? new Date()).getTime()
  const formatter = cached(
    relativeFormats,
    locale,
    () => new Intl.RelativeTimeFormat(BCP47[locale], { numeric: 'auto' }),
  )

  for (const [unit, ms] of UNITS) {
    if (Math.abs(delta) >= ms) {
      return formatter.format(Math.trunc(delta / ms), unit)
    }
  }
  return formatter.format(0, 'second')
}

/** Una lista en prosa: `A, B y C` / `A, B and C`. Nunca un `.join(' y ')`. */
export function formatList(items: readonly string[], locale: Locale = DEFAULT_LOCALE): string {
  return new Intl.ListFormat(BCP47[locale], { style: 'long', type: 'conjunction' }).format(items)
}
